import PropTypes from 'prop-types';
import { COLORS, TYPOGRAPHY, CHAT_TOKENS } from '../../../styles/UISystem';
import { ALL_CONTACT_TYPES, openContactLink } from '../../../utils/contactTypeConfig';
import { shareLineValue, SHARE_LINE_CONTACT_TEXT } from '../../../utils/shareLine';

/**
 * ShareLineBubble — the Profile's share as speech (social-track #3/#4/#13;
 * open-exchange #18: value-only lines).
 *
 * Two lines:
 * - Single-channel: the bare value ("@brilarsen", "212-555-1212") acting
 *   DIRECTLY via the registry's getLink (tel:/mailto:/https) — contract #9a
 *   end-state (2026-07-21): tap the number, get the phone.
 *   Styling (D11, exchange-clarity 2026-07-27 — revises the 07-21
 *   no-underline ruling this far only): RECEIVED values underline — a
 *   newcomer can't otherwise know a bare value acts; tappability IS the
 *   information. Sent bubbles stay plain records (shape/color already says
 *   "yours"); no blanket underlining elsewhere.
 * - Contact link: "My contact" — the phrase is a door to the peer-side
 *   detail page (onContactClick): a multi-share has no single value to act on.
 *
 * Standard bubble geometry/colors (§3.1). Links act only on the RECEIVED side
 * (disableLinks on sent — same rule as ContactShareBubble: your own line is a
 * record of what you said, not a link to yourself).
 *
 * verbatimText variant (device pass 1): the Anon's classified share bubble —
 * their EXACT words as the whole linked segment ("no words in their mouth",
 * but the link rides along for the receiver).
 */
export const ShareLineBubble = ({
  shareType,
  shareValue,
  contactLink = false,
  verbatimText = null,
  isSent,
  disableLinks = false,
  onContactClick,
}) => {
  const spanStyle = disableLinks ? {} : { fontWeight: '500', cursor: 'pointer' };
  // D11: the underline rides only on values that actually ACT (received side,
  // link resolvable) — never on sent records
  const underlineIfActs = (href) =>
    disableLinks || !href ? {} : { textDecoration: 'underline', textUnderlineOffset: '3px' };

  let body;
  if (verbatimText) {
    const href = ALL_CONTACT_TYPES[shareType]?.getLink?.(shareValue) || null;
    body = (
      <span
        style={{ ...spanStyle, ...underlineIfActs(href) }}
        onClick={disableLinks || !href ? undefined : () => openContactLink(href)}
      >
        {verbatimText}
      </span>
    );
  } else if (contactLink) {
    // The whole phrase carries the tap — a split underline read as fussy
    // (device pass 9). UNDERLINED, unlike value lines (device iteration
    // 2026-07-21): this is the one share line that NAVIGATES (door to the
    // card) rather than acts, and the underline is that signal — a phrase
    // has no tappable shape of its own the way a number or @handle does.
    body = (
      <span
        style={
          disableLinks
            ? {}
            : {
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }
        }
        onClick={disableLinks || !onContactClick ? undefined : onContactClick}
      >
        {SHARE_LINE_CONTACT_TEXT}
      </span>
    );
  } else {
    const href = ALL_CONTACT_TYPES[shareType]?.getLink?.(shareValue) || null;
    body = (
      <span
        style={{ ...spanStyle, ...underlineIfActs(href) }}
        onClick={disableLinks || !href ? undefined : () => openContactLink(href)}
      >
        {shareLineValue(shareType, shareValue)}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        marginBottom: '14px',
        width: '100%',
      }}
    >
      <div
        style={{
          padding: '10px 16px',
          borderRadius: isSent
            ? CHAT_TOKENS.BUBBLE_RADIUS_SENT
            : CHAT_TOKENS.BUBBLE_RADIUS_RECEIVED,
          backgroundColor: isSent ? COLORS.CHAT_SENT : COLORS.RECEIVED_GRAY,
          maxWidth: '85%',
          wordBreak: 'break-word',
          ...TYPOGRAPHY.BODY_REGULAR,
          color: isSent ? COLORS.WHITE : COLORS.CHAT_TEXT_DARK,
          textAlign: 'left',
        }}
      >
        {body}
      </div>
    </div>
  );
};

ShareLineBubble.propTypes = {
  verbatimText: PropTypes.string,
  shareType: PropTypes.string,
  shareValue: PropTypes.string,
  contactLink: PropTypes.bool,
  isSent: PropTypes.bool,
  disableLinks: PropTypes.bool,
  onContactClick: PropTypes.func,
};
